import { useMemo, useState } from 'react';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import {
  useRorosSkillMatrix,
  useRorosSkillAssign,
  useRorosSkillUnassign,
  useRorosSkills,
} from '@/hooks/roros/skills';
import type { RorosAgentDefinitionDTO } from '@/services/roros/ai-operators.service';

interface UseActorSkillsParams {
  agentDefinition?: RorosAgentDefinitionDTO | null;
}

/**
 * Owns the AI-operator "skills" concern of UnifiedActorEditor: the skill-matrix
 * and skills-list queries, assign/unassign mutations, the assigned/available
 * derivations, and the assign/unassign handlers. Behaviour-identical extraction
 * (M10f decomposition).
 */
export function useActorSkills({ agentDefinition }: UseActorSkillsParams) {
  const matrixQuery = useRorosSkillMatrix();
  const skillsListQuery = useRorosSkills({ pageSize: 100, isActive: true });
  const assignSkillMutation = useRorosSkillAssign();
  const unassignSkillMutation = useRorosSkillUnassign();
  const [pickerSkillId, setPickerSkillId] = useState('');

  // Matrix format: [{skillId, skillDisplayName, isActive, assignedAgents: [{agentDefinitionId, actorName}]}]
  // Each row is per-SKILL, with agents nested under assignedAgents[].
  const assignedSkills = useMemo(() => {
    if (!agentDefinition?.id || !matrixQuery.data) return [];
    const matrix = (matrixQuery.data as any)?.data ?? matrixQuery.data;
    if (!Array.isArray(matrix)) return [];
    const agentDefId = agentDefinition.id;
    return matrix
      .filter((row: any) => {
        const agents: any[] = row.assignedAgents ?? row.assigned_agents ?? [];
        return agents.some(
          (a: any) => a.agentDefinitionId === agentDefId || a.agent_definition_id === agentDefId,
        );
      })
      .map((row: any) => ({
        skillId: row.skillId ?? row.skill_id,
        skill_id: row.skillId ?? row.skill_id,
        skillDisplayName: row.skillDisplayName ?? row.skill_display_name,
        skill_display_name: row.skillDisplayName ?? row.skill_display_name,
        isActive: row.isActive ?? row.is_active,
        is_active: row.isActive ?? row.is_active,
      }));
  }, [agentDefinition?.id, matrixQuery.data]);

  const assignedSkillIds = useMemo(
    () => new Set(assignedSkills.map((s: any) => s.skillId ?? s.skill_id)),
    [assignedSkills],
  );

  const availableSkillOptions = useMemo(() => {
    // skillsListQuery.data is the array directly (fetchJson unwraps json.data);
    // tolerate a wrapped shape as a fallback.
    const skills = Array.isArray(skillsListQuery.data)
      ? skillsListQuery.data
      : ((skillsListQuery.data as any)?.data ?? []);
    return skills
      .filter((s: any) => !assignedSkillIds.has(s.id))
      .map((s: any) => ({ value: s.id, label: s.displayName ?? s.display_name ?? s.name ?? s.id }));
  }, [skillsListQuery.data, assignedSkillIds]);

  const handleSkillAssign = () => {
    if (!pickerSkillId || !agentDefinition?.id) return;
    assignSkillMutation.mutate(
      { id: pickerSkillId, agentDefinitionId: agentDefinition.id },
      {
        onSuccess: () => { showToast.success(t('Skill assigned')); setPickerSkillId(''); },
        onError: (err) => showToast.error(err instanceof Error ? err.message : t('Failed to assign skill')),
      },
    );
  };

  const handleSkillUnassign = (skillId: string) => {
    if (!agentDefinition?.id) return;
    unassignSkillMutation.mutate(
      { id: skillId, agentDefinitionId: agentDefinition.id },
      {
        onSuccess: () => showToast.success(t('Skill removed')),
        onError: (err) => showToast.error(err instanceof Error ? err.message : t('Failed to remove skill')),
      },
    );
  };

  return {
    matrixQuery,
    assignSkillMutation,
    unassignSkillMutation,
    pickerSkillId,
    setPickerSkillId,
    assignedSkills,
    availableSkillOptions,
    handleSkillAssign,
    handleSkillUnassign,
  };
}
